import { useEffect, useState } from 'react'
import { createLargeCsvAdapter } from './large-csv-adapter.js'
import { COLLAB_LARGE_CSV_EVENT } from './session-events.js'

/**
 * #981 QA 14 — the share view's half of the one rule: a csv/tsv with unsaved LARGE-GRID edits (fragment rows on any
 * base) shows the large grid whatever its size. `GET /api/csv/:id/legacy-edits` on the share session (the route
 * resolves the source as the collab server would for it). Asked once per (file, session); `fileId` null = not asked.
 *
 * `pending` until the answer arrives (the surface waits — the read-only preview would hide the edits). An unreadable
 * answer is "none": both share views of a csv are read-only, so nothing can fork the edits. The server has the last
 * word: a whole-file room it refused as the large grid's (4413, `COLLAB_LARGE_CSV_EVENT` — rows written since the
 * answer, or an answer that could not be read) switches to the large grid. One file body is open at a time here, so
 * any refusal while asking is this file's (its room may be a pointer's source, not this id).
 */
export function useShareFragmentEdits(fileId: string | null, sessionKey: string): { pending: boolean; hasFragmentEdits: boolean } {
  const key = fileId ? `${sessionKey}\u0000${fileId}` : null
  const [answer, setAnswer] = useState<{ key: string; hasFragmentEdits: boolean } | null>(null)
  const [refusedKey, setRefusedKey] = useState<string | null>(null)
  useEffect(() => {
    if (!key) return
    const onLarge = () => setRefusedKey(key)
    window.addEventListener(COLLAB_LARGE_CSV_EVENT, onLarge)
    return () => window.removeEventListener(COLLAB_LARGE_CSV_EVENT, onLarge)
  }, [key])
  useEffect(() => {
    if (!fileId || !key) return
    let live = true
    createLargeCsvAdapter(fileId).legacyEdits()
      .then((r) => { if (live) setAnswer({ key, hasFragmentEdits: r.hasFragmentEdits }) })
      .catch(() => { if (live) setAnswer({ key, hasFragmentEdits: false }) })
    return () => { live = false }
  }, [fileId, key])
  if (!key) return { pending: false, hasFragmentEdits: false }
  if (refusedKey === key) return { pending: false, hasFragmentEdits: true }
  if (answer?.key !== key) return { pending: true, hasFragmentEdits: false }
  return { pending: false, hasFragmentEdits: answer.hasFragmentEdits }
}
